import { useEffect, useRef, type MutableRefObject } from 'react';

interface UseOfflineAccountResyncOnModalOpenOptions {
  /** When true, reset the guard whenever the edit modal opens (not only offline accounts). */
  resetOnModalOpen?: boolean;
}

/** Resets an init guard when the edit modal reopens or `accountId` changes. */
export function useOfflineAccountResyncOnModalOpen(
  modalOpen: boolean,
  accountId: number,
  isOfflineAccount: boolean,
  hasInitializedFromOfflineData: MutableRefObject<boolean>,
  options?: UseOfflineAccountResyncOnModalOpenOptions,
): void {
  const resetOnModalOpen = options?.resetOnModalOpen ?? isOfflineAccount;
  const prevModalOpenRef = useRef(modalOpen);
  const prevAccountIdRef = useRef(accountId);

  if (prevAccountIdRef.current !== accountId) {
    prevAccountIdRef.current = accountId;
    hasInitializedFromOfflineData.current = false;
  }

  useEffect(() => {
    const justOpened = modalOpen && !prevModalOpenRef.current;
    prevModalOpenRef.current = modalOpen;

    if (justOpened && resetOnModalOpen) {
      hasInitializedFromOfflineData.current = false;
    }
  }, [modalOpen, resetOnModalOpen, hasInitializedFromOfflineData]);
}
